import { Fragment, useState } from 'react';
import { Check, XCircle, AlertTriangle, ArrowRight, ArrowUpRight, Quote, ExternalLink, MessageCircle } from 'lucide-react';
import { Reveal } from '@/components/Reveal';
import { useLang, LangLink } from '@/i18n/LangContext';
import { GUIDE_IMAGES } from '@/data/site';
import { PensionLab } from '@/components/PensionLab';
import { TaxSavingsCalculator } from '@/components/TaxSavingsCalculator';

const SectionTitle = ({ title, compact = false }) => (
  <h2 className={`font-heading text-3xl md:text-4xl font-bold tracking-tight text-balance ${compact ? 'mb-0' : 'mb-6'}`}>{title}</h2>
);

const TextSection = ({ section, reverse = false }) => (
  <div className="grid lg:grid-cols-12 gap-8 lg:gap-14 xl:gap-20 items-start">
    <div className={`lg:col-span-5 ${reverse ? 'lg:order-2 lg:pl-4' : ''}`}>
      <SectionTitle title={section.title} compact />
    </div>
    <div className={`lg:col-span-7 space-y-5 text-base md:text-lg leading-relaxed text-foreground/75 ${reverse ? 'lg:order-1' : ''}`}>
      {section.paragraphs?.map((p, i) => <p key={i}>{p}</p>)}
      {section.bullets && (
        <ul className="space-y-3 pt-1">
          {section.bullets.map((b, i) => (
            <li key={i} className="flex items-start gap-3 text-base md:text-lg text-foreground/80">
              <span className="mt-2.5 h-1.5 w-1.5 bg-primary shrink-0" aria-hidden="true" />
              {b}
            </li>
          ))}
        </ul>
      )}
    </div>
  </div>
);

const CardsSection = ({ section }) => (
  <div>
    <SectionTitle title={section.title} />
    {section.lead && <p>{section.lead}</p>}
    <div className={`grid sm:grid-cols-2 gap-4 mt-6 ${section.columns === 3 ? 'lg:grid-cols-3' : ''}`}>
      {section.cards.map((card, i) => {
        const cardContent = (
          <>
            <div className="flex items-center justify-between gap-3 mb-4">
              {card.badge ? (
                <span className="inline-flex rounded-full bg-brand-pale px-3 py-1 text-[11px] uppercase tracking-[0.16em] font-semibold text-primary">{card.badge}</span>
              ) : (
                <span className="font-heading text-sm font-bold text-brand-light">{String(i + 1).padStart(2, '0')}</span>
              )}
              {card.href && <ArrowUpRight className="h-4 w-4 text-brand-light group-hover:text-primary transition-colors" />}
            </div>
            <h3 className="font-heading text-lg md:text-xl font-bold tracking-tight mb-3 group-hover:text-primary transition-colors duration-200">{card.title}</h3>
            <p className="text-sm md:text-base leading-relaxed text-foreground/70">{card.text}</p>
          </>
        );
        const className = "bg-white border border-border rounded-xl p-6 md:p-8 group hover:bg-brand-pale/50 hover:border-primary/40 transition-colors duration-300 h-full";
        return card.href ? (
          <LangLink key={card.title} to={card.href} className={className}>{cardContent}</LangLink>
        ) : (
          <div key={card.title} className={className}>{cardContent}</div>
        );
      })}
    </div>
  </div>
);

const ChecklistSection = ({ section }) => {
  const [checked, setChecked] = useState(() => new Set());
  const { t } = useLang();
  const toggle = (i) => {
    setChecked((prev) => {
      const next = new Set(prev);
      next.has(i) ? next.delete(i) : next.add(i);
      return next;
    });
  };
  return (
    <div>
      <SectionTitle title={section.title} />
      {section.lead && <p>{section.lead}</p>}
      <ul className="mt-4 divide-y divide-border border-t border-b border-border">
        {section.items.map((item, i) => (
          <li key={i}>
            <button
              type="button"
              data-testid={`checklist-item-${section.id}-${i}`}
              onClick={() => toggle(i)}
              aria-pressed={checked.has(i)}
              className="w-full flex items-center gap-4 py-4 text-left group"
            >
              <span
                className={`h-6 w-6 shrink-0 border rounded-md flex items-center justify-center transition-colors duration-200 ${
                  checked.has(i) ? 'bg-primary border-primary text-white' : 'border-input group-hover:border-primary'
                }`}
              >
                {checked.has(i) && <Check className="h-4 w-4" strokeWidth={3} />}
              </span>
              <span className={`text-base md:text-lg transition-colors duration-200 ${checked.has(i) ? 'text-muted-foreground line-through' : 'text-foreground/85'}`}>
                {item}
              </span>
            </button>
          </li>
        ))}
      </ul>
      <p className="mt-3 text-xs text-muted-foreground" data-testid={`checklist-progress-${section.id}`}>
        {t('checklistProgress').replace('{n}', checked.size).replace('{m}', section.items.length)}
      </p>
    </div>
  );
};

const ErrorsSection = ({ section }) => (
  <div>
    <SectionTitle title={section.title} />
    {section.lead && <p>{section.lead}</p>}
    <ul className="mt-4 space-y-4">
      {section.items.map((item, i) => (
        <li key={i} className="flex items-start gap-3.5 bg-white border border-border rounded-lg p-5">
          <XCircle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
          <span className="text-base md:text-lg text-foreground/85">{item}</span>
        </li>
      ))}
    </ul>
  </div>
);

const TimelineSection = ({ section }) => (
  <div>
    <SectionTitle title={section.title} />
    {section.lead && <p>{section.lead}</p>}
    <ol className="mt-6 relative border-l-2 border-brand-light/60 ml-2 space-y-10">
      {section.steps.map((step, i) => (
        <li key={i} className="relative pl-8">
          <span className="absolute -left-[9px] top-1 h-4 w-4 bg-primary border-4 border-background" aria-hidden="true" />
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-primary mb-1.5">{step.label}</p>
          {step.title && <h3 className="font-heading text-lg md:text-xl font-bold tracking-tight mb-2">{step.title}</h3>}
          <p className="text-base leading-relaxed text-foreground/70">{step.text}</p>
        </li>
      ))}
    </ol>
  </div>
);

const WarningSection = ({ section }) => (
  <div data-testid={`warning-${section.id}`} className="border border-primary/20 border-l-4 border-l-primary bg-brand-pale rounded-xl p-6 md:p-8">
    <div className="flex items-center gap-3 mb-3">
      <AlertTriangle className="h-5 w-5 text-primary" />
      <h3 className="font-heading text-lg md:text-xl font-bold tracking-tight">{section.title}</h3>
    </div>
    <p className="text-base leading-relaxed text-foreground/80">{section.text}</p>
  </div>
);

const CaseSection = ({ section }) => {
  const { t } = useLang();
  const c = section.caseStudy;
  const blocks = [
    { label: t('caseSituation'), text: c.situation },
    { label: t('caseProblem'), text: c.problem },
  ];
  return (
    <div className="bg-white border border-border rounded-xl overflow-hidden" data-testid={`case-${section.id}`}>
      <div className="p-6 md:p-8 border-b border-border flex items-center gap-3">
        <Quote className="h-6 w-6 text-brand-light" />
        <h3 className="font-heading text-xl md:text-2xl font-bold tracking-tight">{section.title}</h3>
      </div>
      <div className="grid md:grid-cols-2">
        {blocks.map((b) => (
          <div key={b.label} className="p-6 md:p-8 border-b md:border-b-0 md:odd:border-r border-border">
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-primary mb-3">{b.label}</p>
            <p className="text-sm md:text-base leading-relaxed text-foreground/75">{b.text}</p>
          </div>
        ))}
      </div>
      <div className="grid md:grid-cols-2 border-t border-border">
        <div className="p-6 md:p-8 md:border-r border-border">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-primary mb-4">{t('caseRisks')}</p>
          <ul className="space-y-2.5">
            {c.risks.map((r, i) => (
              <li key={i} className="flex items-start gap-2.5 text-sm md:text-base text-foreground/75">
                <span className="mt-2 h-1.5 w-1.5 bg-destructive shrink-0" />{r}
              </li>
            ))}
          </ul>
        </div>
        <div className="p-6 md:p-8">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-primary mb-4">{t('caseDecisions')}</p>
          <ul className="space-y-2.5">
            {c.decisions.map((d, i) => (
              <li key={i} className="flex items-start gap-2.5 text-sm md:text-base text-foreground/75">
                <Check className="h-4 w-4 text-primary shrink-0 mt-1" />{d}
              </li>
            ))}
          </ul>
        </div>
      </div>
      <div className="p-6 md:p-8 border-t border-border bg-brand-pale/60">
        <p className="text-xs uppercase tracking-[0.2em] font-semibold text-primary mb-3">{t('caseResult')}</p>
        <p className="text-sm md:text-base leading-relaxed text-foreground/80">{c.result}</p>
      </div>
    </div>
  );
};

const PathsSection = ({ section }) => {
  return (
  <div>
    <SectionTitle title={section.title} />
    <div className="grid sm:grid-cols-2 gap-6 mt-6">
      {section.links.map((link) => (
        <LangLink
          key={link.href}
          to={link.href}
          data-testid={`path-link-${link.href.replace(/\//g, '-')}`}
          className="group bg-white border border-border rounded-lg overflow-hidden hover:border-primary transition-colors duration-300 flex flex-col"
        >
          {GUIDE_IMAGES[link.href] && (
            <div className="overflow-hidden aspect-[16/8]">
              <img
                src={GUIDE_IMAGES[link.href]}
                alt=""
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
            </div>
          )}
          <div className="p-6 md:p-7 flex flex-col flex-1">
            <h3 className="font-heading text-lg md:text-xl font-bold tracking-tight mb-3 flex items-start justify-between gap-3 group-hover:text-primary transition-colors duration-200">
              {link.label}
              <ArrowUpRight className="h-5 w-5 text-brand-light group-hover:text-primary group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-[transform,color] duration-200 shrink-0 mt-0.5" />
            </h3>
            <p className="text-sm md:text-base leading-relaxed text-foreground/70">{link.text}</p>
          </div>
        </LangLink>
      ))}
    </div>
  </div>
  );
};

const StatsSection = ({ section }) => (
  <div>
    <SectionTitle title={section.title} />
    {section.lead && <p>{section.lead}</p>}
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-7">
      {section.items.map((item) => (
        <div key={item.label} className="bg-brand-deep text-white rounded-xl p-6 md:p-8">
          <p className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-brand-light mb-3">{item.value}</p>
          <p className="text-sm leading-relaxed text-white/70">{item.label}</p>
        </div>
      ))}
    </div>
    {section.source && (
      <a href={section.source.href} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 mt-5 text-xs font-semibold text-primary hover:underline">
        Fonte: {section.source.label} · verificato il {section.source.verified}
        <ExternalLink className="h-3.5 w-3.5" />
      </a>
    )}
  </div>
);

const ConversionSection = ({ section }) => (
  <div className="bg-brand-deep text-white rounded-2xl p-7 md:p-10 lg:p-12 overflow-hidden relative">
    <div className="relative z-10 max-w-3xl">
      <div className="inline-flex items-center gap-2 text-brand-light text-xs uppercase tracking-[0.2em] font-semibold mb-4">
        <MessageCircle className="h-4 w-4" /> {section.eyebrow || 'Consulenza'}
      </div>
      <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-balance mb-5">{section.title}</h2>
      <p className="text-base md:text-lg leading-relaxed text-white/75 mb-7">{section.text}</p>
      {section.points?.length > 0 && (
        <ul className="grid sm:grid-cols-2 gap-3 mb-8">
          {section.points.map((point) => (
            <li key={point} className="flex items-start gap-2.5 text-sm text-white/80">
              <Check className="h-4 w-4 text-brand-light shrink-0 mt-0.5" /> {point}
            </li>
          ))}
        </ul>
      )}
      <LangLink to={section.href || '/contatti'} className="inline-flex items-center gap-2 bg-brand-light text-brand-deep px-5 py-3 rounded-lg text-sm font-bold hover:bg-white transition-colors">
        {section.label || 'Richiedi una consulenza'} <ArrowRight className="h-4 w-4" />
      </LangLink>
    </div>
  </div>
);

const InlineContentCta = ({ cta, testId }) => {
  const { lang } = useLang();
  return (
    <div data-testid={testId} className="rounded-2xl bg-primary p-7 md:p-9 text-white flex flex-col md:flex-row md:items-center md:justify-between gap-6 overflow-hidden">
      <div className="max-w-2xl">
        <p className="text-xs uppercase tracking-[0.2em] font-semibold text-white/65 mb-3">{lang === 'en' ? 'Personal review' : 'Verifica personale'}</p>
        <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-balance">{cta.title}</h2>
        {cta.text && <p className="mt-3 text-sm md:text-base leading-relaxed text-white/75">{cta.text}</p>}
      </div>
      <LangLink to="/contatti" className="inline-flex items-center justify-center gap-2 rounded-lg bg-white px-5 py-3 text-sm font-bold text-primary hover:bg-brand-pale transition-colors shrink-0">
        {cta.label || (lang === 'en' ? 'Request a review' : 'Richiedi una verifica')} <ArrowRight className="h-4 w-4" />
      </LangLink>
    </div>
  );
};

const RENDERERS = {
  text: TextSection,
  cards: CardsSection,
  checklist: ChecklistSection,
  errors: ErrorsSection,
  timeline: TimelineSection,
  warning: WarningSection,
  case: CaseSection,
  paths: PathsSection,
  stats: StatsSection,
  pensionLab: PensionLab,
  taxSavings: ({ section }) => <TaxSavingsCalculator source={section.id || 'content'} />,
  conversion: ConversionSection,
};

export const ContentSections = ({ sections, layout = 'article', inlineCta }) => {
  let textSectionIndex = 0;
  const ctaAfterIndex = sections.length <= 3 ? 1 : 2;
  return (
    <div className={layout === 'area' ? '' : 'space-y-16 md:space-y-24'}>
      {sections.map((section, index) => {
        const Renderer = RENDERERS[section.type];
        if (!Renderer) return null;
        const reverse = section.type === 'text' && textSectionIndex++ % 2 === 1;
        const showInlineCta = inlineCta && index === ctaAfterIndex;
        const sectionNode = layout === 'area' ? (
          <section
            id={section.id}
            className={`scroll-mt-28 py-20 md:py-28 ${index % 2 === 1 ? 'bg-white border-y border-border' : ''}`}
            data-testid={`section-${section.id}`}
          >
            <div className="mx-auto max-w-site px-5 md:px-8">
              <Reveal><Renderer section={section} reverse={reverse} /></Reveal>
            </div>
          </section>
        ) : (
          <section id={section.id} className="scroll-mt-28" data-testid={`section-${section.id}`}>
            <Reveal>
              <Renderer section={section} reverse={reverse} />
            </Reveal>
          </section>
        );

        return (
          <Fragment key={section.id}>
            {sectionNode}
            {showInlineCta && (layout === 'area' ? (
              <section className="py-8 md:py-12">
                <div className="mx-auto max-w-site px-5 md:px-8">
                  <InlineContentCta cta={inlineCta} testId={`inline-content-cta-${index}`} />
                </div>
              </section>
            ) : (
              <InlineContentCta cta={inlineCta} testId={`inline-content-cta-${index}`} />
            ))}
          </Fragment>
        );
      })}
    </div>
  );
};

export const RelatedGuides = ({ pages }) => {
  const { t } = useLang();
  if (!pages?.length) return null;
  return (
    <section data-testid="related-guides" className="mt-20 md:mt-28 pt-12 border-t border-border">
      <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight mb-8">{t('relatedTitle')}</h2>
      <div className="grid sm:grid-cols-2 gap-4">
        {pages.map((p) => (
          <LangLink key={p.path} to={p.path} data-testid={`related-${p.path.replace(/\//g, '-')}`} className="bg-white border border-border rounded-lg p-6 group hover:bg-brand-pale/50 transition-colors duration-300">
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-brand-light mb-2">{p.area}</p>
            <h3 className="font-heading text-lg font-bold tracking-tight mb-3 group-hover:text-primary transition-colors duration-200 text-balance">{p.title}</h3>
            <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary">
              {t('readGuide')} <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
            </span>
          </LangLink>
        ))}
      </div>
    </section>
  );
};
